import React, { useMemo } from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import SectionHeader from 'design-system/SectionHeader';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatPlural } from 'utils/formatting';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useCurrentPrice } from '../hooks/selectors';
import { isWeb } from '../../../constants';
const strings = {
    toolTipTitle: 'Analyst price targets',
    toolTipSubTitle: 'Analyst price targets are their projection of a stock’s future price. When setting a stock’s price target, an analyst looks at a number of factors including the financial health of the company, management, earnings releases, sector trends, company’s future plans and more.\n\nAnalysts use this information to determine what the stock is worth and what the price will be in 12 or 18 months.\n\nEmma does not carry out any investment research, has no influence on the price targets displayed here and will never give you advice on what to invest in.\n\nData is sourced by Refinitiv.',
};
const LINE_CONTAINER_PADDING = 2 * rem(2);
const MARKER_WIDTH = rem(20);
const INFO_VIEW_WIDTH = rem(64);
const CURRENT_MARKER_WIDTH = rem(8);
const AnalystPriceTarget = ({ assetId, currency, ratingsTotal, priceTargetLow, priceTargetMean, priceTargetHigh, }) => {
    const currentPrice = useCurrentPrice(assetId);
    const styles = useStyles(styleSet);
    const { width } = useAppFrameDimensions(isWeb);
    const priceTargetInfo = useMemo(() => {
        if (!currentPrice ||
            currency === undefined ||
            priceTargetLow === undefined ||
            priceTargetMean === undefined ||
            priceTargetHigh === undefined) {
            return undefined;
        }
        const widthInUse = width - rem(64);
        /**
         * This is the case where the current price falls between priceTargetLow and priceTargetHigh
         */
        if (currentPrice.value >= priceTargetLow && currentPrice.value <= priceTargetHigh) {
            const minValue = Math.min(currentPrice.value, priceTargetLow);
            const maxValue = Math.max(currentPrice.value, priceTargetHigh);
            const range = maxValue - minValue;
            const lowCalculatedFraction = (priceTargetLow - minValue) / range;
            const highCalculatedFraction = (priceTargetHigh - minValue) / range;
            const meanCalculatedFraction = (priceTargetMean - minValue) / range;
            const currentCalculateFraction = (currentPrice.value - minValue) / range;
            const priceLowInfoViewLeft = 0;
            const lowPriceLeft = lowCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
            const meanPriceLeft = meanCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
            const highPriceLeft = highCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
            const currentPriceLeft = currentCalculateFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
            const priceTargetInfoViewLeft = meanCalculatedFraction * (widthInUse - INFO_VIEW_WIDTH);
            const priceHighInfoViewLeft = widthInUse - INFO_VIEW_WIDTH;
            const assumedPriceCurrentInfoViewLeft = currentPriceLeft + MARKER_WIDTH / 2 - INFO_VIEW_WIDTH / 2;
            const assumedCurrentPriceMarkerLeft = currentPriceLeft + MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2;
            if (currentPrice.value > priceTargetMean) {
                return {
                    lowPriceLeft,
                    meanPriceLeft,
                    highPriceLeft,
                    currentPriceLeft,
                    priceLowInfoViewLeft,
                    priceHighInfoViewLeft,
                    priceTargetInfoViewLeft,
                    priceCurrentInfoViewLeft: Math.min(widthInUse - INFO_VIEW_WIDTH, assumedPriceCurrentInfoViewLeft),
                    currentPriceMarkerLeft: Math.min(assumedCurrentPriceMarkerLeft, highPriceLeft + MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2),
                    priceRangeLineLeft: meanPriceLeft,
                    priceRangeLineRight: widthInUse - currentPriceLeft - MARKER_WIDTH - LINE_CONTAINER_PADDING,
                };
            }
            if (currentPrice.value < priceTargetMean) {
                return {
                    lowPriceLeft,
                    meanPriceLeft,
                    highPriceLeft,
                    currentPriceLeft,
                    priceLowInfoViewLeft,
                    priceHighInfoViewLeft,
                    priceTargetInfoViewLeft,
                    priceCurrentInfoViewLeft: Math.max(0, assumedPriceCurrentInfoViewLeft),
                    currentPriceMarkerLeft: Math.max(assumedCurrentPriceMarkerLeft, MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2),
                    priceRangeLineLeft: currentPriceLeft,
                    priceRangeLineRight: widthInUse - meanCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING) - rem(20 + 4),
                };
            }
            return {
                lowPriceLeft,
                meanPriceLeft,
                highPriceLeft,
                currentPriceLeft,
                priceLowInfoViewLeft,
                priceHighInfoViewLeft,
                priceTargetInfoViewLeft,
                priceCurrentInfoViewLeft: assumedPriceCurrentInfoViewLeft,
                currentPriceMarkerLeft: assumedCurrentPriceMarkerLeft,
                priceRangeLineLeft: meanPriceLeft,
                priceRangeLineRight: widthInUse - meanPriceLeft - MARKER_WIDTH - LINE_CONTAINER_PADDING,
            };
        }
        /**
         * This is the case where the current price is higher than priceTargetHigh
         */
        if (currentPrice.value > priceTargetHigh) {
            const targetToHighToCurrentPriceRatio = (priceTargetHigh - priceTargetMean) / (currentPrice.value - priceTargetHigh);
            /**
             * Display mean, high and current price
             */
            if (targetToHighToCurrentPriceRatio > 0.3) {
                const maxValue = currentPrice.value;
                const minValue = priceTargetMean;
                const range = maxValue - minValue;
                const highCalculatedFraction = (priceTargetHigh - minValue) / range;
                const highPriceLeft = highCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
                const priceHighInfoViewLeft = highPriceLeft + MARKER_WIDTH / 2 - INFO_VIEW_WIDTH / 2;
                return {
                    highPriceLeft,
                    meanPriceLeft: 0,
                    currentPriceLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH,
                    priceTargetInfoViewLeft: 0,
                    priceHighInfoViewLeft,
                    priceCurrentInfoViewLeft: widthInUse - INFO_VIEW_WIDTH,
                    currentPriceMarkerLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH + MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2,
                    priceRangeLineLeft: 0,
                    priceRangeLineRight: 0,
                    highMarkerHighlight: true,
                };
            }
            /**
             * Display target and current price
             */
            return {
                meanPriceLeft: 0,
                currentPriceLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH,
                currentPriceMarkerLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH + MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2,
                priceTargetInfoViewLeft: 0,
                priceCurrentInfoViewLeft: widthInUse - INFO_VIEW_WIDTH,
                priceRangeLineLeft: 0,
                priceRangeLineRight: 0,
            };
        }
        /**
         * This is the case where the current price is lower than priceTargetLow
         */
        const currentToLowToTargetPriceRatio = (priceTargetMean - priceTargetLow) / (priceTargetLow - currentPrice.value);
        /**
         * Display current, low and mean price
         */
        if (currentToLowToTargetPriceRatio > 0.3) {
            const minValue = currentPrice.value;
            const maxValue = priceTargetMean;
            const range = maxValue - minValue;
            const lowCalculatedFraction = (priceTargetLow - minValue) / range;
            const lowPriceLeft = lowCalculatedFraction * (widthInUse - rem(20) - LINE_CONTAINER_PADDING);
            const priceLowInfoViewLeft = lowPriceLeft + MARKER_WIDTH / 2 - INFO_VIEW_WIDTH / 2;
            return {
                lowPriceLeft,
                currentPriceLeft: 0,
                meanPriceLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH,
                priceTargetInfoViewLeft: widthInUse - INFO_VIEW_WIDTH,
                priceLowInfoViewLeft,
                priceCurrentInfoViewLeft: 0,
                currentPriceMarkerLeft: MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2,
                priceRangeLineLeft: 0,
                priceRangeLineRight: 0,
                lowMarkerHighlight: true,
            };
        }
        /**
         * Display target and current price
         */
        return {
            currentPriceLeft: 0,
            meanPriceLeft: widthInUse - LINE_CONTAINER_PADDING - MARKER_WIDTH,
            currentPriceMarkerLeft: MARKER_WIDTH / 2 - CURRENT_MARKER_WIDTH / 2,
            priceCurrentInfoViewLeft: 0,
            priceTargetInfoViewLeft: widthInUse - INFO_VIEW_WIDTH,
            priceRangeLineLeft: 0,
            priceRangeLineRight: 0,
        };
    }, [currency, currentPrice, priceTargetHigh, priceTargetLow, priceTargetMean, width]);
    if (!priceTargetInfo || !ratingsTotal) {
        return undefined;
    }
    return (<>
      <SectionHeader horizontalMargins largeVerticalMargins toolTipTitle={strings.toolTipTitle} toolTipSubTitle={strings.toolTipSubTitle}>
        Analyst price targets
      </SectionHeader>
      <CardView noMarginBottom noMarginTop withPadding>
        <Text Numbers-24>{formatAmount(priceTargetMean, currency)}</Text>
        <Spacer h={4}/>
        <Text TextThin-14 Secondary>
          ~12 month avg. target • {ratingsTotal} {formatPlural('analyst', 'analysts', ratingsTotal)}
        </Text>
        <Spacer h={24}/>

        <View style={styles.priceContainerRow}>
          {priceTargetInfo.priceLowInfoViewLeft !== undefined && (<View style={[styles.priceContainer, { left: priceTargetInfo.priceLowInfoViewLeft }]}>
              <Text Numbers-14>{formatAmount(priceTargetLow, currency)}</Text>
              <Text TextThin-12 Secondary>
                Low
              </Text>
            </View>)}

          {priceTargetInfo.priceTargetInfoViewLeft !== undefined && (<View style={[styles.priceContainer, { left: priceTargetInfo.priceTargetInfoViewLeft }]}>
              <Text Numbers-14>{formatAmount(priceTargetMean, currency)}</Text>
              <Text TextThin-12 Secondary>
                Target
              </Text>
            </View>)}

          {priceTargetInfo.priceHighInfoViewLeft !== undefined && (<View style={[styles.priceContainer, { left: priceTargetInfo.priceHighInfoViewLeft }]}>
              <Text Numbers-14>{formatAmount(priceTargetHigh, currency)}</Text>
              <Text TextThin-12 Secondary>
                High
              </Text>
            </View>)}
        </View>

        <Spacer h={16}/>
        <View style={styles.barWrapper}>
          <View style={styles.barContainer}/>

          <View style={[
            styles.bgMarker,
            { left: priceTargetInfo.priceRangeLineLeft, right: priceTargetInfo.priceRangeLineRight },
        ]}/>

          {priceTargetInfo.lowPriceLeft !== undefined && (<View testID="lowPriceMarkerView" style={[styles.priceMarker, { left: priceTargetInfo.lowPriceLeft + rem(2) }]}>
              {priceTargetInfo.lowMarkerHighlight ? (<View testID="lowPriceMarkerViewInner" style={styles.priceMarkerHighlighted}/>) : null}
            </View>)}
          {priceTargetInfo.meanPriceLeft !== undefined && (<View testID="meanPriceMarkerView" style={[styles.priceMarker, { left: priceTargetInfo.meanPriceLeft + rem(2) }]}/>)}
          {priceTargetInfo.highPriceLeft !== undefined && (<View testID="highPriceMarkerView" style={[styles.priceMarker, { left: priceTargetInfo.highPriceLeft + rem(2) }]}>
              {priceTargetInfo.highMarkerHighlight ? (<View testID="highPriceMarkerViewInner" style={styles.priceMarkerHighlighted}/>) : null}
            </View>)}
          {/* Could be useful for testing...do not delete */}
          {/* <View style={[styles.priceMarker, { left: priceTargetInfo.currentPriceLeft + rem(2) }]} /> */}

          <View style={[styles.currentPriceContainer, { left: priceTargetInfo.priceCurrentInfoViewLeft }]}>
            <Text Numbers-14>{formatAmount(currentPrice?.value, currency)}</Text>
            <Text TextThin-12 Secondary>
              Current
            </Text>
          </View>

          {priceTargetInfo.currentPriceMarkerLeft !== undefined && (<View testID="currentPriceLineMarker" style={[styles.currentPriceMarker, { left: priceTargetInfo.currentPriceMarkerLeft + rem(2) }]}>
              <View style={styles.markerOuterCircle}>
                <View style={styles.markerInnerCircle}/>
              </View>
              <View style={styles.lineMarkerContainer}>
                <View style={styles.markerLineOuter}>
                  <View style={styles.markerLineInner}/>
                </View>
              </View>
            </View>)}
        </View>
      </CardView>
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    barWrapper: {
        paddingBottom: rem(38 + 16),
    },
    barContainer: {
        height: rem(28),
        borderRadius: rem(28),
        backgroundColor: colors.background.dark,
    },
    priceContainerRow: {
        flex: 1,
        height: rem(38),
    },
    priceContainer: {
        top: 0,
        bottom: 0,
        height: rem(38),
        position: 'absolute',
        alignItems: 'center',
        width: INFO_VIEW_WIDTH,
    },
    currentPriceContainer: {
        bottom: 0,
        height: rem(38),
        position: 'absolute',
        alignItems: 'center',
        width: INFO_VIEW_WIDTH,
    },
    priceMarker: {
        top: rem(4),
        width: rem(20),
        height: rem(20),
        padding: rem(1),
        position: 'absolute',
        borderRadius: rem(20),
        backgroundColor: colors.cards.onDark,
    },
    priceMarkerHighlighted: {
        flex: 1,
        borderRadius: rem(20),
        backgroundColor: colors.elements.brand,
    },
    bgMarker: {
        top: rem(2),
        height: rem(24),
        position: 'absolute',
        borderRadius: rem(20),
        backgroundColor: colors.elements.brand,
    },
    currentPriceMarker: {
        width: rem(8),
        bottom: rem(40),
        height: rem(26),
        position: 'absolute',
    },
    markerOuterCircle: {
        width: rem(8),
        height: rem(8),
        alignItems: 'center',
        borderRadius: rem(6),
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    markerInnerCircle: {
        width: rem(6),
        height: rem(6),
        borderRadius: rem(6),
        backgroundColor: colors.elements.brand,
    },
    markerLineOuter: {
        flex: 1,
        width: rem(4),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    markerLineInner: {
        flex: 1,
        width: rem(2),
        borderRadius: rem(6),
        backgroundColor: colors.elements.brand,
    },
    lineMarkerContainer: {
        bottom: 0,
        left: rem(2),
        top: rem(7),
        width: rem(4),
        position: 'absolute',
    },
}));
export default AnalystPriceTarget;
